import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Heading from "../../components/text/Heading";
import Button from "../../components/button/Button";
import QuestionnaireForm from "../../components/Form/QuestionnaireForm";
import QuestionnaireTable from "../../components/table/QuestionnaireTable";
import InputField from "../../components/Form/InputField";
import QuestionnaireAPI from "../../API/QuestionnaireAPI/QuestionnaireAPI";
import {
  getTemplatesStart,
  getTemplatesSuccess,
  getTemplatesFailure,
  createTemplateStart,
  createTemplateSuccess,
  createTemplateFailure,
  updateTemplateStart,
  updateTemplateSuccess,
  updateTemplateFailure,
  deleteTemplateStart,
  deleteTemplateSuccess,
  deleteTemplateFailure,
  setSearchTerm,
  clearFilters,
  setPagination,
  clearMessages,
} from "../../redux/reducer/QuestionnaireReducer";

const Questionnaire = () => {
  const dispatch = useDispatch();
  const {
    templates,
    templatesLoading,
    createFormLoading,
    updateFormLoading,
    deleteLoading,
    pagination,
    searchTerm,
    successMessage,
    errorMessage,
  } = useSelector((state) => state.questionnaire);

  // Local state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const fetchTemplates = useCallback(
    async (page = 1, search = searchTerm, filters = {}) => {
      try {
        dispatch(getTemplatesStart());

        const params = {
          page,
          limit: pagination.itemsPerPage,
          search,
          ...filters,
        };

        const response = await QuestionnaireAPI.getAllQuestionnaireTemplates(
          params
        );

        // Extract pagination info from backend response
        const { templates, paginationInfo } = response.data;

        // Update both templates and pagination
        dispatch(
          getTemplatesSuccess({ templates, pagination: paginationInfo })
        );
      } catch (error) {
        dispatch(
          getTemplatesFailure(error.message || "Failed to fetch templates")
        );
      }
    },
    [dispatch, searchTerm, pagination.itemsPerPage]
  );

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        dispatch(clearMessages());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage, dispatch]);

  const handleCreateTemplate = async (formData) => {
    try {
      dispatch(createTemplateStart());
      const response = await QuestionnaireAPI.createQuestionnaireTemplate(
        formData
      );
      dispatch(createTemplateSuccess(response.data));
      setShowCreateModal(false);
      fetchTemplates();
    } catch (error) {
      dispatch(
        createTemplateFailure(error.message || "Failed to create template")
      );
    }
  };

  const handleUpdateTemplate = async (formData) => {
    try {
      dispatch(updateTemplateStart());
      const response = await QuestionnaireAPI.updateQuestionnaireTemplate(
        editingTemplate._id,
        formData
      );
      dispatch(updateTemplateSuccess(response.data));
      setShowEditModal(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      dispatch(
        updateTemplateFailure(error.message || "Failed to update template")
      );
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      dispatch(deleteTemplateStart());
      const response = await QuestionnaireAPI.deleteQuestionnaireTemplate(
        deletingTemplateId
      );
      dispatch(deleteTemplateSuccess(response.data));
      setShowDeleteModal(false);
      setDeletingTemplateId(null);
      fetchTemplates();
    } catch (error) {
      dispatch(
        deleteTemplateFailure(error.message || "Failed to delete template")
      );
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowEditModal(true);
  };

  const handleDelete = (templateId) => {
    setDeletingTemplateId(templateId);
    setShowDeleteModal(true);
  };

  const handleSearch = () => {
    dispatch(setSearchTerm(searchInput));
    fetchTemplates(1, searchInput);
  };

  const handlePageChange = (page) => {
    dispatch(setPagination({ currentPage: page }));
    fetchTemplates(page, searchTerm);
  };

  const clearAllFilters = () => {
    setSearchInput("");
    dispatch(clearFilters());
    dispatch(setSearchTerm(""));
    fetchTemplates(1, "", {});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Heading heading="Questionnaire Management" />
              <p className="mt-2 text-sm text-gray-600">
                Create and manage questionnaire templates for users
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <Button
                text="Create Template"
                onClick={() => setShowCreateModal(true)}
                customClass="w-full sm:w-auto"
              />
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 sm:px-0 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div>
                <InputField
                  id="search"
                  label="Search Questions"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search in questions..."
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  mainClass=""
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-4 pt-4 border-t">
              <Button
                text="Search"
                onClick={handleSearch}
                customClass="bg-gray-500 hover:bg-gray-600"
              />
              <Button
                text="Clear Filters"
                onClick={clearAllFilters}
                customClass="bg-gray-500 hover:bg-gray-600"
              />
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="px-4 sm:px-0 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="px-4 sm:px-0 mb-6">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="px-4 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <QuestionnaireTable
              questionnaires={templates}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isLoading={templatesLoading}
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              limit={pagination.perPage}
              onLimitChange={(newLimit) => {
                dispatch(setPagination({ ...pagination, perPage: newLimit }));
                fetchTemplates(1, searchTerm);
              }}
              totalItems={pagination.totalItems}
            />
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="relative max-h-[90vh] overflow-auto rounded-lg bg-white shadow-sm max-w-2xl w-full mx-4">
            <div className="px-8 py-4">
              <h5 className="text-center text-xl mb-3 font-semibold tracking-wide text-black">
                Create Questionnaire Template
              </h5>
              <QuestionnaireForm
                onSubmit={handleCreateTemplate}
                onCancel={() => setShowCreateModal(false)}
                isLoading={createFormLoading}
                mode="create"
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="relative max-h-[90vh] overflow-auto rounded-lg bg-white shadow-sm max-w-2xl w-full mx-4">
            <div className="px-8 py-4">
              <h5 className="text-center text-xl mb-3 font-semibold tracking-wide text-black">
                Edit Questionnaire Template
              </h5>
              <QuestionnaireForm
                initialData={editingTemplate}
                onSubmit={handleUpdateTemplate}
                onCancel={() => {
                  setShowEditModal(false);
                  setEditingTemplate(null);
                }}
                isLoading={updateFormLoading}
                mode="edit"
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="relative w-1/3 max-h-[90vh] overflow-auto rounded-lg bg-white shadow-md">
            <div className="px-6 py-4">
              <h5 className="text-center text-xl font-semibold text-gray-800">
                Confirm Deletion
              </h5>
              <div className="flex justify-center mt-4">
                <svg
                  className="h-12 w-12 text-red-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 110-16 8 8 0 010 16zm-1-11a1 1 0 112 0v4a1 1 0 11-2 0v-4zm1 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-center text-gray-600 mt-4">
                This action cannot be undone. The template will be soft deleted
                and hidden from users.
              </p>
              <div className="mt-6 flex justify-center space-x-4">
                <Button
                  text="Delete"
                  onClick={handleDeleteTemplate}
                  isLoading={deleteLoading}
                  disabled={deleteLoading}
                  customClass="bg-red-500 hover:bg-red-600"
                />
                <Button
                  text="Cancel"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingTemplateId(null);
                  }}
                  customClass="bg-gray-200 hover:bg-gray-300 text-gray-800"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Questionnaire;
